import React, { Component, Fragment, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useDebounce } from "use-debounce";
import I18n from "i18n-js";

import api from "../api";

import useStore from "../store";

import useRoutingState from "../hooks/useRoutingState";
import useMembersPlansState from "../hooks/useMembersPlansState";

import ActionButton from "../commons/ActionButton";
import PageTitle from "../commons/PageTitle";

import PdpSelector from "./components/PdpSelector";
import MemberPlanSelector from "./components/MemberPlanSelector";
import PlansTable from "./components/PlansTable";
import CreatePlanModal from "./components/Modal/CreatePlanModal";

import plusIcon from "../assets/icons/plus.svg";

const All = ({
  user,
  teams,
  team,
  personal,
  isTeamLeadOrAbove,
  isTeamLead,
  members,
  showBack,
  resume,
  isResumeUploadNotified
}) => {
  useRoutingState();

  const { ready, selectedMember, setMemberUuid } = useMembersPlansState(members);

  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingDebounced] = useDebounce(loading, 500);

  const [plans, setPlans] = useState([]);

  const { showModal } = useStore(state => state.modal);

  // If the user has no resume and has not been notified about it yet,
  // show the resume upload modal.
  useEffect(() => {
    if (!Boolean(resume) && !isResumeUploadNotified) {
      showModal("set-resume-modal");
    }
  }, []);

  // Initially, the selectedMember is null, then load plans for current logged user.
  // Every time selectedMember changes, load plans for the new selected user.
  useEffect(() => {
    const loadPlansForUser = async () => {
      if (ready) {
        await loadPlans(selectedMember?.uuid)
      }
    }

    loadPlansForUser();
  }, [ready, selectedMember]);

  const loadPlans = async (userUuid = null) => {
    try {
      setLoading(true);
      const res = await api.get(`/development_plans?user_uuid=${userUuid}`);
      const plans = res?.data?.plans;
      setPlans(plans);
      setLoading(false);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        toast.error(<b>{err.response.data.message}</b>);
      } else {
        toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
      }
      setLoading(false);
      console.error(err);
    }
  }

  const showCreatePlan = () => {
    setShowCreatePlanModal(true);
  }

  const hideCreatePlan = () => {
    setShowCreatePlanModal(false);
  }

  const renderPageHeader = () => (
    <div className="pdp-row page-header">
      <PageTitle
        title={I18n.t("pdp.all.title")}
        selectors={
          <div className="selectors">
            <PdpSelector
              all
              teams={teams}
              selectedTeam={team}
              personal={personal}
              isTeamLeadOrAbove={isTeamLeadOrAbove}
            />
            {members?.length ? (
              <MemberPlanSelector
                team={team}
                members={members}
                showMyPlan={isTeamLead}
                isMemberSelected={member => selectedMember?.uuid === member?.uuid}
                onMemberClick={member => setMemberUuid(member?.user_uuid)}
              />
            ) : null}
          </div>
        }
        showBack={showBack}
      />
      <div className="action-buttons">
        <ActionButton
          title={I18n.t("pdp.actions.plan.new")}
          icon={plusIcon}
          onClick={showCreatePlan}
        />
      </div>
    </div>
  );

  const renderPageContent = () => (
    <div className="page-content">
      <PlansTable
        loading={loadingDebounced}
        user={user}
        plans={plans}
        isTeamLead={isTeamLead}
      />
    </div>
  );

  return (
    <Fragment>
      <div className="pdp all">
        <div className="container-fluid">
          {renderPageHeader()}
          <div className="pdp-row">
            {renderPageContent()}
          </div>
        </div>
      </div>
      <CreatePlanModal
        show={showCreatePlanModal}
        team={team}
        onHide={hideCreatePlan}
      />
      <Toaster
        position="top-right"
        reverseOrder
      />
    </Fragment>
  );
}

// NOTE: needed now to current version of ReactOnRails to work with hooks
export default class AllComponent extends Component {
  render() {
    return <All {...this.props} />;
  }
}